import {
  isUsingEvidon, isUsingOneTrust,
} from '@sie/web-common-react-components';
import getQueryParam from './utils/getQueryParam';
import initTelemetry from './utils/telemetry';
import navTelemetrySpecs from './utils/telemetry/specs/nav';
import resolvePrimaryNavLocation from './utils/telemetry-js/getLocationScene';

const getIdForTelemetry = (element) => {
  const elementId = element.id;
  const stringIdMarker = 'msg-';

  return elementId.slice(elementId.indexOf(stringIdMarker) + stringIdMarker.length);
};

const getSeededTelemetryData = (element) => {
  const telemetryData = element.getAttribute('data-telemetry');

  if (!telemetryData) {
    return {};
  }

  try {
    return JSON.parse(telemetryData);
  } catch (e) {
    return {};
  }
};

const getSearchBarTelemetryPlatform = (host = '') => {
  if (/^(webstore|store)(\..+)?\.playstation\.com$/.test(host)) {
    return 'web-store';
  }

  if (/^library(\..+)?\.playstation\.com$/.test(host)) {
    return 'myps';
  }

  return 'pdc';
};

const getTopLevelHeaderName = element => (
  (element.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase()
);

const addTelemetryDataToSecondary = (parentEl, category, topLevelHeaderName, telemetry) => {
  const secondaryLinks = [].slice.call(parentEl.querySelectorAll('.shared-nav__secondary-anchor'));

  secondaryLinks.forEach((secondaryLink) => {
    const linkName = getIdForTelemetry(secondaryLink);
    const {
      subLevelHeaderName,
      interactCta,
    } = getSeededTelemetryData(secondaryLink);
    const secondaryLinkLabel = (secondaryLink.textContent || '').replace(/\s+/g, ' ').trim();
    const resolvedSubLevelHeader = secondaryLinkLabel || subLevelHeaderName || interactCta;
    const resolvedInteractCta = resolvedSubLevelHeader
      ? `${topLevelHeaderName || category}:${resolvedSubLevelHeader}`
      : interactCta;

    const data = {
      category,
      linkName,
      ...(resolvedSubLevelHeader && {
        interactAction: `select header:${topLevelHeaderName || category}:${resolvedSubLevelHeader}`,
      }),
      ...(resolvedInteractCta && { interactCta: resolvedInteractCta }),
    };

    secondaryLink.setAttribute('data-telemetry', telemetry.makeEvent({ type: 'shared-nav:click:nav-item', data }));
  });

  const quickLinks = [].slice.call(parentEl.querySelectorAll('.shared-nav__link-list-anchor'));

  quickLinks.forEach((quickLink) => {
    const linkName = quickLink.innerText.replace(/\W/g, '');

    const data = {
      category,
      linkName,
    };

    quickLink.setAttribute('data-telemetry', telemetry.makeEvent({ type: 'shared-nav:click:nav-item', data }));
  });
};

const addTelemetryDataToPrimary = (telemetry) => {
  const { document } = window;
  const logoLink = document.querySelector('.shared-nav-ps-logo-link');
  const primaryNavDropdownControls = [].slice.call(document.querySelectorAll('.shared-nav__primary-button, .shared-nav__primary-anchor'));
  const referrer = getQueryParam(window.location.search, 'smcid');
  const marketingCampaign = getQueryParam(window.location.search, 'emcid');

  if (logoLink) {
    const data = {
      category: 'header',
      linkName: 'ps home',
      interactAction: 'select ps home icon',
      interactCta: 'home icon',
    };

    logoLink.setAttribute('data-telemetry', telemetry.makeEvent({ type: 'shared-nav:click:nav-item', data }));
  }

  primaryNavDropdownControls.forEach((primaryNav) => {
    const category = getIdForTelemetry(primaryNav);
    const mobileNavIconName = getTopLevelHeaderName(primaryNav);
    const data = {
      category,
      marketingCampaign,
      referrer,
    };

    if (primaryNav.tagName.toLowerCase() === 'button') {
      primaryNav.setAttribute('data-telemetry', telemetry.makeEvent({
        type: 'shared-nav:page-view',
        data: { ...data, mobileNavIconName },
      }));
    }

    if (primaryNav.tagName.toLowerCase() === 'a') {
      data.linkName = category;
      data.interactAction = `select header:${mobileNavIconName}`;
      data.interactCta = mobileNavIconName;
      primaryNav.setAttribute('data-telemetry', telemetry.makeEvent({ type: 'shared-nav:click:nav-item', data }));
    }

    addTelemetryDataToSecondary(primaryNav.parentNode, category, mobileNavIconName, telemetry);
  });
};

const addTelemetryDataToSearch = (telemetry) => {
  const { document, location: { host = '' } = {} } = window;
  const searchButton = document.querySelector('.shared-nav-icon--search');

  if (!searchButton) return;

  const platform = getSearchBarTelemetryPlatform(host);
  const data = {
    category: 'search',
    interactAction: `select ${platform} search bar`,
    interactCta: `${platform}:search bar`,
  };

  searchButton.setAttribute('data-telemetry', telemetry.makeEvent({ type: 'shared-nav:click:nav-item', data }));
};

const addTelemetryDataToNav = (telemetry) => {
  addTelemetryDataToPrimary(telemetry);
  addTelemetryDataToSearch(telemetry);
};

/**
 * Sets up the telemetry system for use with jetstream (shared nav)
 */
export default (webToolbarType) => {
  const telemetry = initTelemetry({
    scope: 'shared-nav',
    resolveLocation: resolvePrimaryNavLocation,
  });

  telemetry.addSpec('shared-nav', navTelemetrySpecs);
  telemetry.privacyPolicy = '';
  addTelemetryDataToNav(telemetry);

  requestIdleCallback(() => {
    import('./utils/telemetry-js')
      .then(({ default: initTelemetryJs }) => {
        const setupTelemetryJs = () => (initTelemetryJs({ telemetry, webToolbarType }));
        setupTelemetryJs();

        if (isUsingEvidon() || isUsingOneTrust()) {
          const eventName = isUsingEvidon() ? 'evidon' : 'cookie';

          // Reset telemetry on consent change
          window.document.addEventListener(`${eventName}ConsentState`, setupTelemetryJs);
        }
      });
  }, { timeout: 5000 });

  return telemetry;
};
